import { createSlice } from '@reduxjs/toolkit';

export const TOUR_TYPES = {
    BANK_ACCOUNT: 'bank_account',
    BANK_UPLOAD: 'bank_upload',
    INVENTORY_UPLOAD: 'inventory_upload',
    BANK_DROPDOWN: 'bank_dropdown',
    INVENTORY_REVIEW: 'inventory_review',
    EMAIL_FORWARDING: 'email_forwarding',
    SKYBOX_INTEGRATION: 'skybox_integration',
};

export const TOUR_METADATA = {
    [TOUR_TYPES.BANK_DROPDOWN]: {
        name: 'Step 1: Bank Connection Tour',
        description: 'Learn how to connect to your bank accounts',
        steps: ['dropdown', 'plaid-option', 'manual-option'],
        totalSteps: 3,
    },
    [TOUR_TYPES.BANK_ACCOUNT]: {
        name: 'Step 2: Manual Bank Account Tour',
        description: 'To start this tour click on Create Bank Account button in the drop down menu',
        steps: [
            'bank-form-name',
            'bank-form-type',
            'bank-form-detail-type',
            'bank-form-mask-number',
            'bank-form-submit',
        ],
        totalSteps: 5,
    },
    [TOUR_TYPES.BANK_UPLOAD]: {
        name: 'Step 3: Bank Upload Tour',
        description:
            'To start this tour upload your bank transaction file in upload transaction tab',
        steps: ['upload-area', 'account-selection', 'mapping-fields', 'import-button'],
        totalSteps: 4,
    },

    [TOUR_TYPES.INVENTORY_REVIEW]: {
        name: 'Step 4: Inventory Reconciliation Tour',
        description: 'Learn how to use the For Review tab',
        steps: ['for-review-tab', 'file-upload-tab', 'agent-match-button'],
        totalSteps: 3,
    },

    [TOUR_TYPES.INVENTORY_UPLOAD]: {
        name: 'Step 5: Inventory Upload Tour',
        description:
            'To start this tour go to inventory reconcilliation file upload tab and upload purchase activity file from your POS system',
        steps: ['upload', 'activity', 'mapping'],
        totalSteps: 3,
    },

    [TOUR_TYPES.EMAIL_FORWARDING]: {
        name: 'Step 6: Email Forwarding Setup',
        description: 'Learn how to set up email forwarding for receipt processing',
        steps: ['email-field'],
        totalSteps: 1,
    },
    [TOUR_TYPES.SKYBOX_INTEGRATION]: {
        name: 'Step 7: SkyBox POS Integration',
        description: 'Learn how to connect and set up your SkyBox POS integration',
        steps: ['pos-navigation', 'skybox-connect-button', 'skybox-token-link', 'skybox-token-fields', 'skybox-start-date', 'skybox-submit-button'],
        totalSteps: 6,
    },
};

const loadTourStateFromStorage = () => {
    try {
        const storedState = localStorage.getItem('tourState');
        if (storedState) {
            return JSON.parse(storedState);
        }
    } catch (error) {
        console.error('Error loading tour state from localStorage', error);
    }
    return {};
};

const initialState = {
    tours: loadTourStateFromStorage(),
    tutorialPreference: null,
    tutorialPreferenceLoaded: false,
};

const tourSlice = createSlice({
    name: 'tours',
    initialState,
    reducers: {
        startTour: (state, action) => {
            const { tourId, isManual = false } = action.payload;

            state.tours[tourId] = {
                ...state.tours[tourId],
                started: true,
                completed: false,
                skipped: false,
                currentStep: 0,
                // isManualStart: isManual,
                lastUpdated: new Date().toISOString(),
            };

            localStorage.setItem('tourState', JSON.stringify(state.tours));
        },

        startTourManually: (state, action) => {
            const { tourId } = action.payload;
            state.tours[tourId] = {
                ...state.tours[tourId],
                started: true,
                completed: false,
                skipped: false,
                currentStep: 0,
                manuallyStarted: true,
                lastUpdated: new Date().toISOString(),
            };
            localStorage.setItem('tourState', JSON.stringify(state.tours));
        },

        updateTourStep: (state, action) => {
            const { tourId, stepIndex } = action.payload;
            if (state.tours[tourId]) {
                state.tours[tourId] = {
                    ...state.tours[tourId],
                    currentStep: stepIndex,
                    // manuallyStarted: false, // Clear manual flag on any step update
                    lastUpdated: new Date().toISOString(),
                };
                localStorage.setItem('tourState', JSON.stringify(state.tours));
            }
        },

        completeTour: (state, action) => {
            const { tourId } = action.payload;
            if (state.tours[tourId]) {
                state.tours[tourId] = {
                    ...state.tours[tourId],
                    completed: true,
                    skipped: false,
                    manuallyStarted: false,
                    lastUpdated: new Date().toISOString(),
                };

                localStorage.setItem('tourState', JSON.stringify(state.tours));
            }
        },

        resetTour: (state, action) => {
            const { tourId } = action.payload;
            if (state.tours[tourId]) {
                delete state.tours[tourId];

                localStorage.setItem('tourState', JSON.stringify(state.tours));
            }
        },

        resetAllTours: (state) => {
            state.tours = {};
            state.tutorialPreference = null;
            localStorage.setItem('tourState', JSON.stringify(state.tours));
        },

        skipAllTours: (state) => {
            Object.values(TOUR_TYPES).forEach((tourId) => {
                state.tours[tourId] = {
                    ...state.tours[tourId],
                    started: false,
                    completed: false,
                    skipped: true,
                    currentStep: 0,
                    manuallyStarted: false,
                    lastUpdated: new Date().toISOString(),
                };
            });
            localStorage.setItem('tourState', JSON.stringify(state.tours));
        },

        interruptTour: (state, action) => {
            const { tourId } = action.payload;
            if (
                state.tours[tourId] &&
                state.tours[tourId].started &&
                !state.tours[tourId].completed
            ) {
                delete state.tours[tourId];
                localStorage.setItem('tourState', JSON.stringify(state.tours));
            }
        },

        goToNextTour: (state, action) => {
            const { currentTourId, nextTourId } = action.payload;

            if (state.tours[currentTourId]) {
                state.tours[currentTourId] = {
                    ...state.tours[currentTourId],
                    completed: true,
                    skipped: false,
                    manuallyStarted: false,
                    lastUpdated: new Date().toISOString(),
                };
            }

            state.tours[nextTourId] = {
                ...state.tours[nextTourId],
                started: true,
                completed: false,
                skipped: false,
                currentStep: 0,
                manuallyStarted: false,
                lastUpdated: new Date().toISOString(),
            };

            localStorage.setItem('tourState', JSON.stringify(state.tours));
        },

        setTutorialPreference: (state, action) => {
            const { preference } = action.payload;
            state.tutorialPreference = preference;
            state.tutorialPreferenceLoaded = true;
        },
    },
});

export const {
    startTour,
    startTourManually,
    updateTourStep,
    completeTour,
    resetTour,
    resetAllTours,
    skipAllTours,
    interruptTour,
    goToNextTour,
    setTutorialPreference,
} = tourSlice.actions;

export const selectAllTours = (state) => state.tours.tours;
export const selectTourStatus = (state, tourId) => state.tours.tours[tourId] || null;
export const selectTutorialPreferenceLoaded = (state) => state.tours.tutorialPreferenceLoaded;
export const selectTutorialPreference = (state) => state.tours.tutorialPreference;
export const selectIsSkipped = (state, tourId) => state.tours.tours[tourId]?.skipped || false;
export const selectIsCompleted = (state, tourId) => state.tours.tours[tourId]?.completed || false;

export default tourSlice.reducer;
